import type { RESTAzarFeedbackException, RPCAzarFeedbackException } from './AzarFeedbackException';
import type {
  RESTNotEnoughFreeQuotaException,
  RESTNotEnoughGemException,
  RPCPrivilegedActionException,
} from './PrivilegedActionException';
import type { RPCAuthenticationException } from './RPCAuthenticationException';
import type { RPCBadCredentialsException } from './RPCBadCredentialsException';
import type { RPCInsufficientInformationException } from './RPCInsufficientInformationException';
import type { RPCNotFriendException } from './RPCNotFriendException';
import type { RPCPeerVersionException } from './RPCPeerVersionException';
import { RPCPunishedException } from './RPCPunishedException';
import { RPCVerifyEmailNeedException } from './RPCVerifyEmailNeedException';

export const RPC_ERROR_CODES = {
  /* API 상에서 정의된 예외 발생시 부여되는 에러코드 (0~9999) */
  INTERNAL_SERVER_ERROR: -32000,

  /* 권한에 관련된 에러 코드 (100~199) */

  /** 로그인이 되어 있지 않은 경우 발생하는 에러.
   * @see AuthenticationException */
  AUTHENTICATION_ERROR: 101,

  /** 유료 행동(PrivilegedAction)을 하는데 실패한 경우 발생하는 에러.
   * @see PrivilegedActionException */
  PRIVILEGED_ACTION_ERROR: 102,

  /** 리캡차 요청이 요구되는 경우 발생하는 에러
   * @see RecaptchaRequiredException */
  RECAPTCHA_REQUIRED: 103,

  /* 가입 및 로그인에 관련된 에러 코드 (200~299) */

  /**
   * 계정이 이미 존재하여 가입에 실패한 경우 발생하는 에러.
   * @see AccountAlreadyExistException
   */
  ACCOUNT_ALREADY_EXIST: 201,

  /** 차단(blocked)된 기기로 가입/로그인을 시도한 경우 발생하는 에러.
   * @see DeviceBlockedException */
  DEVICE_BLOCKED: 202,

  /** 영구차단(banned)된 계정으로 로그인을 시도한 경우 발생하는 에러.
   * @see AccountBannedException */
  ACCOUNT_BANNED: 203,

  /** 일시적인 로그인 불가능 상태(잠금상태)인 경우 발생하는 에러.
   * @see AccountLockedException */
  ACCOUNT_LOCKED: 204,

  /**  일시정지(suspend)된 계정으로 로그인을 시도한 경우 발생하는 에러.
   * @see AccountLockedException */
  ACCOUNT_SUSPENDED: 205,

  /** 이메일주소/패스워드 쌍이나 로그인 토큰에 해당하는 계정을 찾을 수 없는 경우 발생하는 에러.
   * @see AccountLockedException */
  BAD_CREDENTIALS: 206,

  /** 서비스가 점검중이여서 가입/로그인이 불가능한 경우 발생하는 에러.
   * @see ServiceMaintenanceException */
  SERVICE_IN_MAINTENANCE: 207,

  /** 이메일주소/패스워드 쌍이나 로그인 토큰에 해당하는 계정을 찾을 수 없는 경우 발생하는 에러.
   * @see AccountLockedException */
  INSUFFICIENT_INFORMATION: 208,

  /**
   * web-client 의 경우 로그인시도 시점에 ip 기반으로 판단하여 허용된 특정 국가가 아닐 경우 발생하는 에러.
   */
  LOGIN_NOT_SUPPORTED_COUNTRY: 209,

  /* 서비스의 사용과 관련된 에러 코드 (300~999) */

  /** 클라이언트에게 서비스 부정이용에 따른 경고를 보내기 위해서 사용하는 에러.
   * @see AbuseWarningException */
  ABUSE_WARNING: 301,

  /** (S3에 파일을 올린다음 호출되는 API의 경우) 서버가 해당 파일을 찾을 수 없을 때 발생하는 에러.
   * @see FileNotFoundException */
  FILE_NOT_FOUND: 302,

  /** 친구신청이 서버측에서 거부되었음을 나타내는 에러.
   * @see FriendRejectedException */
  FRIEND_REJECTED: 303,

  /** 승낙된(=ACCEPTED) 친구관계에서만 가능한 작업이 요청되었을때 발생하는 에러.
   * @see NotFriendException */
  NOT_FRIEND: 304,

  /** 유저가 요청한 특정 행동의 상대방의 클라이언트 앱 버전이 해당 행동을 받아주기 위한 최소 버전에 미달할 때 발생하는 에러.
   * @see PeerVersionException */
  PEER_VERSION_ERROR: 305,

  /**
   * 아자르 ID 등록시 정책에 어긋나는 경우 발생하는 에러.
   */
  AZAR_ID_POLICY: 306,

  /** Error code for an IAB purchase that is not actually processed for the current account.
   * @see AccountMismatchException */
  ACCOUNT_MISMATCH: 307,

  /** Error code for an App Store IAB purchase without proper receipt.
   * @see TransactionNotFoundException */
  TRANSACTION_NOT_FOUND: 308,

  /**
   * 서비스 사용 중 클라이언트에 특정 메시지를 보여주는 에러.
   * @see AzarFeedbackException
   */
  AZAR_FEEDBACK: 309,

  /**
   * 나이의 상한/하한선에 걸려 가입 불가 등의 메세지를 보여줄때 에러 코드
   * @see AzarIllegalAgeException
   */
  AZAR_AGE: 310,

  /**
   * 클라이언트의 필터 정보(FilterInfo)가 최신이 아닌 경우 발생하는 에러.
   */
  STALE_FILTER_ERROR: 311,

  /**
   * 서비스 사용 중 기기 이전에 대한 이전 사용자 내역 삭제 팝업을 띄워주기 위해 사용되는 에러 코드.
   * @see DeviceTransferException
   */
  DEVICE_TRANSFER: 312,

  /**
   * 서비스 사용 중 클라이언트에 특정 메시지를 보여주는 에러.
   * @see AccountNotExistException
   */
  ACCOUNT_NOT_EXIST: 313,

  /**
   * 클라이언트에 제재에 대한 안내를 노출하기 위해 사용되는 에러 코드.
   * @see PunishedException
   */
  PUNISHED: 314,

  /**
   * Azar Pick 의 송신자 혹은 수신자의 대기열 큐가 가득찼을 때 사용되는 에러 코드.
   */
  AZAR_PICK_QUEUE_EXCEED: 315,

  /**
   * Azar Pick 의 신청자가 아닌 경우 사용되는 에러 코드
   */
  AZAR_PICK_NOT_FOUND: 316,

  /**
   * Azar Pick 의 신청자가 특정 유저에 Azar Pick 신청을 했는데, 이미 해당 유저가 대기열 큐에 존재하는 경우 사용되는 에러 코드.
   */
  AZAR_PICK_ALREADY_REQUESTED: 317,

  /**
   * Azar Pick 의 신청자가 특정 유저에 Azar Pick 신청을 했는데, 대기열 큐 상태가 PAUSED 인 경우 사용되는 에러 코드.
   */
  AZAR_PICK_QUEUE_PAUSED: 318,

  /* 외부 연동 관련 에러 코드 (2000~2999) */
  /**
   * Error on instagram accoutn already exist while registering instagram account.
   */
  INSTAGRAM_ACCOUNT_ALREDAY_EXIST: 2001,

  /**
   * Error code for not verified Facebook login for web client.
   * @see NotVerifiedException
   */
  NOT_VERIFIED: 2002,

  /**
   * Error code for not process kmc verification
   * @see NotKmcVerifiedException
   */
  NOT_KMC_VERIFIED: 2003,

  /**
   * Error code for not process email verification
   * @see VerifyEmailNeedException
   */
  NOT_EMAIL_VERIFIED: 2004,

  /**
   * Error code for mismatch user's input email and already saved email is different
   * @see EmailMismatchException
   */
  EMAIL_MISMATCH: 2005,

  /**
   * Error code for send email or phone verification code is failed
   * @see EmailMismatchException
   */
  SEND_VERIFY_CODE: 2006,

  /**
   * Error code for SignUpUserInformationLackException
   * @see SignUpUserInformationLackException
   */
  SIGNUP_USER_INFORMATION_LACK: 2007,

  /**
   * 클라이언트가 앱을 업그레이드 해야할때 발생하는 에러.
   * @see UpdateRequiredException
   */
  UPDATE_REQUIRED_ERROR: 2008,

  /* 범용적으로 사용되는 JAVA 예외에 대해서 부여되는 에러 코드 (10000~) */

  /** API 호출 시 주어진 argument 값이 비정상적인 경우 발생하는 에러.
   * @see IllegalArgumentException */
  ILLEGAL_ARGUMENT_ERROR: 10001,

  /** 해당 API 호출을 처리할 수 없는 상태인 경우 발생하는 에러.
   * @see IllegalStateException */
  ILLEGAL_STATE_ERROR: 10002,

  /**
   * JSON-RPC Specification 에 의해 정해져 있는 예약된 에러 코드
   * @see {@link https://www.jsonrpc.org/specification}
   */

  /** JSON parse error */
  PARSE_ERROR: -32700,

  /** invalid request */
  INVALID_REQUEST: -32600,

  /** method not found */
  METHOD_NOT_FOUND: -32601,

  /** method parameters invalid */
  METHOD_PARAMS_INVALID: -32602,

  /** internal error */
  INTERNAL_ERROR: -32603,

  /** error not handled */
  ERROR_NOT_HANDLED: -32001,

  /** bulk error */
  BULK_ERROR: -32002,

  /** client timeout */
  CLIENT_TIMEOUT: -32003,
} as const;

export type RPCErrorCodes = (typeof RPC_ERROR_CODES)[keyof typeof RPC_ERROR_CODES];

export interface RPCException<E, T = string> {
  /** A Number that indicates the error type that occurred. */
  code: RPCErrorCodes;
  /** A String providing a short description of the error. */
  message: string;

  data?: {
    exceptionTypeName: T;
    throwable: E;
  };
}

export type AzarRPCExceptions =
  | RPCAuthenticationException
  | RPCBadCredentialsException
  | RPCAzarFeedbackException
  | RPCNotFriendException
  | RPCPeerVersionException
  | RPCPrivilegedActionException
  | RPCInsufficientInformationException
  | RPCVerifyEmailNeedException
  | RPCPunishedException;

/**  @see {https://git.dev.hpcnt.com/hyperconnect/azar/blob/a0ca6a5052251c8c458ce9ff3f13aa8737c4b243/azar-api-impl/src/main/java/com/azarlive/api/web/common/response/ErrorCode.kt#L14} */
export const REST_ERROR_CODES = {
  // 기본 에러 코드
  GONE: [410, 'GONE'],
  BAD_REQUEST: [400, 'BAD_REQUEST'],
  UNAUTHORIZED: [401, 'UNAUTHORIZED'],
  FORBIDDEN: [403, 'FORBIDDEN'],
  NOT_FOUND: [404, 'NOT_FOUND'],
  THROTTLED: [429, 'THROTTLED'],
  INTERNAL_SERVER_ERROR: [500, 'INTERNAL_SERVER_ERROR'],

  // 인증 관련 에러 코드
  INVALID_GRANT: [401, 'INVALID_GRANT'],

  // 공통 기본 비즈니스 에러 코드
  COUNT_LIMIT_EXCEEDED: [400, 'COUNT_LIMIT_EXCEEDED'],
  LENGTH_LIMIT_EXCEEDED: [400, 'LENGTH_LIMIT_EXCEEDED'],
  EXPIRED: [400, 'EXPIRED'],

  // 공통 비즈니스 도메인 (결제, 아이템, 제재)
  NOT_ENOUGH_GEMS: [402, 'NOT_ENOUGH_GEMS'],
  NOT_ENOUGH_FREE_QUOTA: [402, 'NOT_ENOUGH_FREE_QUOTA'],
  NO_ITEM: [402, 'NO_ITEM'],
  DUPLICATED_ITEM: [409, 'DUPLICATED_ITEM'],
  PUNISHED: [403, 'PUNISHED'],
  FEEDBACK_EXCEPTION: [400, 'FEEDBACK_EXCEPTION'],
  USER_DELETED: [400, 'USER_DELETED'],
  NOT_VALID_PASSWORD: [400, 'NOT_VALID_PASSWORD'],
  UPGRADE_REQUIRED: [426, 'UPGRADE_REQUIRED'],

  // 피크타임 도메인
  PEAK_TIME_EVENT_NOT_EXISTS: [404, 'PEAK_TIME_EVENT_NOT_EXISTS'],

  // 인증 도메인
  VERIFICATION_FAILED: [400, 'VERIFICATION_FAILED'],

  // Captcha 도메인
  RECAPTCHA_REQUIRED: [403, 'RECAPTCHA_REQUIRED'],
  TURNSTILE_REQUIRED: [403, 'TURNSTILE_REQUIRED'],

  // 매치 필터 도메인
  MATCH_FILTER_OPTION_COST_MISMATCH: [400, 'MATCH_FILTER_OPTION_COST_MISMATCH'],
  MATCH_FILTER_OPTION_NOT_EXISTS: [400, 'MATCH_FILTER_OPTION_NOT_EXISTS'],
  MATCH_FILTER_FILTER_NOT_EXISTS: [400, 'MATCH_FILTER_FILTER_NOT_EXISTS'],

  // Email 도메인
  NOT_VALID_EMAIL: [400, 'NOT_VALID_EMAIL'],
  VERIFY_CODE_MISMATCH: [400, 'VERIFY_CODE_MISMATCH'],
  EMAIL_MISMATCH: [400, 'EMAIL_MISMATCH'],
  VERIFY_COUNT_EXCEED: [429, 'VERIFY_COUNT_EXCEED'],
  EMAIL_VERIFIED: [400, 'EMAIL_VERIFIED'],
  SEND_CODE_FAIL: [500, 'SEND_CODE_FAIL'],

  // invite business error code
  FAILED_INVITE: [400, 'FAILED_INVITE'],

  // Follow 도메인
  ALREADY_FOLLOWED: [400, 'ALREADY_FOLLOWED'],
  NOT_FOLLOWED: [400, 'NOT_FOLLOWED'],
  FOLLOW_EXCEED_LIMIT: [400, 'FOLLOW_EXCEED_LIMIT'],
  BLOCKED_OPEN_MESSAGE: [400, 'BLOCKED_OPEN_MESSAGE'],

  /**
   * Promotion Code 도메인
   * 클라이언트에서 message 를 유저에게 노출한다.
   * @see com.azarlive.domain.service.promotion.exception.PromotionCodeExceptionService.ResponseCode
   */
  PROMOTION_CODE_USE_EXCEPTION: [400, 'PROMOTION_CODE_USE_EXCEPTION'],
} as const;

export type RESTErrorCodes = (typeof REST_ERROR_CODES)[keyof typeof REST_ERROR_CODES];

export interface RESTFailure<T> {
  code: RESTErrorCodes[1];
  message: string;
  data: T | null;
}

export type AzarRESTFailures =
  | RESTAzarFeedbackException
  | RESTNotEnoughGemException
  | RESTNotEnoughFreeQuotaException;
